import React, { useState, useEffect, useRef } from 'react';
import { Navbar, Nav, Container, Button, NavDropdown, Image, Form, FormControl } from 'react-bootstrap';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { FaUser, FaShoppingCart, FaHeart, FaBox, FaStore, FaChartBar, FaEnvelope, FaCog, FaSignOutAlt, FaSearch, FaUserCircle, FaShoppingBag, FaTachometerAlt, FaComments, FaUserCog, FaPowerOff, FaSignInAlt, FaUserPlus } from 'react-icons/fa';
import { useAuth } from '../../contexts/AuthContext';
import { useCart } from '../../contexts/CartContext';
import { useMobileMenu } from '../../contexts/MobileMenuContext';
import SearchBar from '../common/SearchBar';
import logoImage from '../../assets/logo.png';
import './Navbar.css';

const HomeNavbar = () => {
  const { currentUser, logout, userRole } = useAuth();
  const { getItemCount } = useCart();
  const navigate = useNavigate();
  const location = useLocation();
  const { isMobileMenuOpen: mobileMenuOpen, toggleMobileMenu: setMobileMenuOpen } = useMobileMenu();
  const [searchQuery, setSearchQuery] = useState('');
  const menuRef = useRef(null);
  const toggleRef = useRef(null);
  const navbarRef = useRef(null);

  // Handle navigation with proper same-page detection
  const handleNavigation = (path, event) => {
    // Prevent navigation if already on the same page
    if (location.pathname === path) {
      console.log('Already on page, just refreshing:', path);
      event?.preventDefault();
      // Close mobile menu if it's open
      if (mobileMenuOpen) {
        setMobileMenuOpen(false);
      }
      return;
    }
    
    console.log('Navigating to:', path);
    navigate(path);
    
    // Close mobile menu after navigation
    setTimeout(() => {
      if (mobileMenuOpen) {
        setMobileMenuOpen(false);
      }
    }, 100);
  };

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (mobileMenuOpen && 
          navbarRef.current && 
          !navbarRef.current.contains(event.target)) {
        setMobileMenuOpen(false);
        // Also close Bootstrap's native collapse
        const collapseElement = document.getElementById('basic-navbar-nav');
        if (collapseElement) {
          collapseElement.classList.remove('show');
        }
      }
    };

    // Add event listener when component mounts
    document.addEventListener('mousedown', handleClickOutside);
    
    // Clean up event listener when component unmounts
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [mobileMenuOpen, setMobileMenuOpen]);
  
  const handleSearch = (e) => {
    e.preventDefault();
    const searchTerm = e.target.elements.search.value;
    navigate(`/products?search=${searchTerm}`);
  };
  
  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
    } catch (error) {
      console.error('Failed to log out', error);
    } 
  };

  return (
    <div className="sticky-top">
      {/* Mobile menu backdrop overlay */}
      {mobileMenuOpen && (
        <div className="mobile-menu-backdrop" />
      )}

      {/* Main Navbar */}
      <Navbar expand="lg" className="navbar-shadow navbar-gradient" ref={navbarRef}>
        <Container>
          {/* UniTrade Logo/Brand */}
          <Navbar.Brand as={Link} to="/" className="navbar-brand-container">
            <div className="d-flex align-items-center">
              <img 
                src={logoImage} 
                alt="UniTrade Logo" 
                className="navbar-logo"
              />
              <span className="brand-text">UniTrade</span>
            </div>
          </Navbar.Brand>
          
          {/* Desktop Login/Register Buttons - Bootstrap desktop layout */}
          {!currentUser && (
            <Nav className="d-none d-lg-flex ms-auto">
              <Nav.Link as={Link} to="/login" className="nav-link">
                <FaSignInAlt className="me-2" /> Login
              </Nav.Link>
              <Nav.Link as={Link} to="/register" className="nav-link">
                <FaUserPlus className="me-2" /> Register
              </Nav.Link>
            </Nav>
          )}
          
          {/* Search Bar - Desktop - Absolutely centered */}
          <div className="d-none d-lg-block position-absolute" style={{ 
            left: '50%', 
            transform: 'translateX(-50%)', 
            width: '450px',
            maxWidth: '40vw'
          }}>
            <SearchBar 
              placeholder="Search products..."
              className="w-100"
              value={searchQuery}
              onChange={setSearchQuery}
            />
          </div>
          
          {/* Mobile Menu Toggle - Bootstrap's native hamburger */}
          <Navbar.Toggle 
            aria-controls="basic-navbar-nav" 
            className="d-lg-none"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            ref={toggleRef}
            aria-expanded={mobileMenuOpen}
          >
            <div className="d-flex flex-column justify-content-around" style={{ width: '24px', height: '24px' }}>
              <span className="d-block bg-white rounded" style={{ height: '2px', width: '100%' }}></span>
              <span className="d-block bg-white rounded" style={{ height: '2px', width: '100%' }}></span>
              <span className="d-block bg-white rounded" style={{ height: '2px', width: '100%' }}></span>
            </div>
          </Navbar.Toggle>
        </Container>
        
        {/* Bootstrap's Native Navbar Collapse */}
        <Navbar.Collapse id="basic-navbar-nav">
          {currentUser ? (
            <Nav className="flex-column">
              {/* Cart Icon (for buyers) */}
              {userRole === 'buyer' && (
                <Nav.Link 
                  as="button" 
                  onClick={(e) => handleNavigation('/cart', e)} 
                  className="nav-icon-link"
                >
                  <FaShoppingCart className="nav-icon" />
                  {getItemCount() > 0 && (
                    <span className="badge bg-warning rounded-pill ms-1 nav-badge">
                      {getItemCount()}
                    </span>
                  )}
                </Nav.Link>
              )}
              
              {/* User Profile Icon */}
              {currentUser && (
                <Nav.Link 
                  as="button" 
                  onClick={(e) => handleNavigation('/profile', e)} 
                  className="nav-icon-link"
                >
                  <FaUser className="nav-icon" />
                </Nav.Link>
              )}
            </Nav>
          ) : (
            <Nav className="d-lg-none">
              <Nav.Link 
                as="button" 
                onClick={(e) => {
                  if (location.pathname !== '/login') {
                    handleNavigation('/login', e);
                  }
                }}
                className="d-flex align-items-center"
                disabled={location.pathname === '/login'}
                style={{ 
                  opacity: location.pathname === '/login' ? 0.5 : 1,
                  cursor: location.pathname === '/login' ? 'not-allowed' : 'pointer',
                  pointerEvents: location.pathname === '/login' ? 'none' : 'auto'
                }}
              >
                <FaSignInAlt className="me-2" /> Login
              </Nav.Link>
              <Nav.Link 
                as="button" 
                onClick={(e) => {
                  if (location.pathname !== '/register') {
                    handleNavigation('/register', e);
                  }
                }}
                className="d-flex align-items-center"
                disabled={location.pathname === '/register'}
                style={{ 
                  opacity: location.pathname === '/register' ? 0.5 : 1,
                  cursor: location.pathname === '/register' ? 'not-allowed' : 'pointer',
                  pointerEvents: location.pathname === '/register' ? 'none' : 'auto'
                }}
              >
                <FaUserPlus className="me-2" /> Register
              </Nav.Link>
            </Nav>
          )}
        </Navbar.Collapse>
      </Navbar>
    </div>
  );
};

export default HomeNavbar;
